import { ArrowBack, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import ReviewForm from '../../components/frontend/reviews/ReviewForm';
import StarRating from '../../components/frontend/reviews/StarRating';
import { amazonColors } from '../../config/amazonTheme';
import { canReviewOrder, getUserReviewForOrder, submitReview } from '../../services/reviewService';
import { useAuthStore } from '../../store/useAuthStore';
import { CanReviewResponseDto, CreateReviewDto, ReviewDto } from '../../types/review';
import { CreateReviewFormData } from '../../validation/reviewSchema';

type PageState = 'loading' | 'can-review' | 'already-reviewed' | 'cannot-review' | 'submitted';

const CustomerReviewPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const hasChecked = useRef(false);
  const { token, user } = useAuthStore();
  const { t: myTranslate } = useTranslation();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [canReviewData, setCanReviewData] = useState<CanReviewResponseDto | null>(null);
  const [existingReview, setExistingReview] = useState<ReviewDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (hasChecked.current) return;

    if (!token) {
      navigate('/login', { state: { from: `/review/order/${orderId}` } });
      return;
    }

    if (!orderId) {
      setPageState('cannot-review');
      setErrorMessage(myTranslate('reviews.invalidOrderId'));
      return;
    }

    hasChecked.current = true;
    checkCanReview();
  }, [orderId, token]);

  const checkCanReview = async () => {
    try {
      // Check if user can review
      const canReviewResult = await canReviewOrder(orderId!);
      setCanReviewData(canReviewResult);

      if (canReviewResult.canReview) {
        setPageState('can-review');
      } else {
        // Check if they already reviewed
        const existingReviewResult = await getUserReviewForOrder(orderId!);
        if (existingReviewResult) {
          setExistingReview(existingReviewResult);
          setPageState('already-reviewed');
        } else {
          setPageState('cannot-review');
          setErrorMessage(canReviewResult.reason || myTranslate('reviews.cannotReviewFallback'));
        }
      }
    } catch {
      setPageState('cannot-review');
      setErrorMessage(myTranslate('reviews.verifyEligibilityFailed'));
    }
  };

  const handleSubmitReview = async (formData: CreateReviewFormData, photos?: File[]) => {
    if (!orderId) return;

    setIsSubmitting(true);
    try {
      const dto: CreateReviewDto = {
        orderId,
        rating: formData.rating,
        reviewerName: formData.reviewerName,
        comment: formData.comment || undefined,
        email: formData.email || undefined,
        highlights: formData.highlights,
        wouldOrderAgain: formData.wouldOrderAgain || undefined,
        agreeToPublish: formData.agreeToPublish,
      };

      await submitReview(dto, photos);
      setPageState('submitted');
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Cannot review state
  if (pageState === 'cannot-review') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {myTranslate('reviews.cannotReviewOrder')}
          </Typography>
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            {errorMessage}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/profile', { state: { initialSection: 'orders' } })}
          >
            {myTranslate('reviews.backToOrders')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Already reviewed state
  if (pageState === 'already-reviewed' && existingReview) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {myTranslate('reviews.alreadyReviewedOrder')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {myTranslate('reviews.alreadyReviewedDescription')}
          </Typography>

          <Box
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              textAlign: 'left',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2">{myTranslate('reviews.yourRating')}</Typography>
              <StarRating value={existingReview.rating} readonly size="small" />
            </Box>
            {existingReview.comment && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {myTranslate('reviews.yourReview')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  "{existingReview.comment}"
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              {myTranslate('reviews.submittedOn', { date: new Date(existingReview.createdAt).toLocaleDateString() })}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={() => navigate('/profile', { state: { initialSection: 'orders' } })}
            sx={{
              bgcolor: amazonColors.orange,
              '&:hover': { bgcolor: amazonColors.orangeHover || '#e67a00' },
            }}
          >
            {myTranslate('reviews.backToOrders')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Submitted state
  if (pageState === 'submitted') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {myTranslate('reviews.thankYouForReview')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {myTranslate('reviews.feedbackHelpsOthers')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/profile', { state: { initialSection: 'orders' } })}
            sx={{
              bgcolor: amazonColors.orange,
              '&:hover': { bgcolor: amazonColors.orangeHover || '#e67a00' },
            }}
          >
            {myTranslate('reviews.backToOrders')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Can review - show form
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/profile', { state: { initialSection: 'orders' } })}
          sx={{ mb: 2 }}
        >
          {myTranslate('reviews.backToOrders')}
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <ReviewForm
          orderNumber={canReviewData?.orderNumber || ''}
          customerName={user?.firstName || user?.email?.split('@')[0]}
          onSubmit={handleSubmitReview}
          isLoading={isSubmitting}
        />
      </Paper>
    </Container>
  );
};

export default CustomerReviewPage;
